import React from 'react';
import { Account } from '../types';
import { MapPinIcon, BuildingIcon } from 'lucide-react';
import { useUser } from '../lib/userContext';
import { logAuditEvent } from '../lib/auditLog';
interface AccountCardProps {
  account: Account;
  onOpenPartnerView: (botType?: 'internal' | 'external') => void;
  onShowLocationIntel?: () => void;
}
export function AccountCard({
  account,
  onOpenPartnerView,
  onShowLocationIntel
}: AccountCardProps) {
  const {
    currentUser
  } = useUser();
  const healthColors = {
    Green: 'bg-emerald-500',
    Yellow: 'bg-yellow-500',
    Red: 'bg-red-500'
  };
  const teamCount = account.people?.length || 0;
  const productCount = account.products_services.length;
  const handleCardClick = () => {
    console.log('[AccountCard] Card clicked');
    console.log('[AccountCard] Account:', {
      id: account.id,
      name: account.name
    });
    logAuditEvent('view_account', account.name, currentUser.name);
    onOpenPartnerView();
  };
  const handleMapClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onShowLocationIntel) {
      onShowLocationIntel();
    }
  };
  return <button onClick={handleCardClick} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl overflow-hidden hover:border-slate-700/80 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-500 shadow-lg shadow-black/40 text-left w-full" data-demo="account-card" data-id="element-34">
      {/* Decorative gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-35" />
      {/* Content */}
      <div className="relative p-6" data-id="element-36">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6" data-id="element-37">
          <div className="flex items-start gap-4 flex-1 min-w-0" data-id="element-38">
            <div className="w-16 h-16 flex-shrink-0 bg-slate-800/60 rounded-xl p-2 border border-slate-700/40" data-id="element-39">
              <BuildingIcon className="w-full h-full text-slate-400" data-id="element-40" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-41">
              <h3 className="text-xl font-bold text-white mb-1 truncate group-hover:text-blue-400 transition-colors duration-300" data-id="element-42">
                {account.name}
              </h3>
              <p className="text-sm text-slate-400" data-id="element-43">{account.industry}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-44">
            <div className={`w-2.5 h-2.5 rounded-full ${healthColors[account.health]} shadow-lg`} data-id="element-45" />
            <button onClick={handleMapClick} className="p-2 rounded-lg bg-slate-800/60 hover:bg-blue-600 border border-slate-700/40 hover:border-blue-500/40 transition-all duration-200 hover:scale-110 group/map" title="View location intelligence" data-id="element-46">
              <MapPinIcon className="w-4 h-4 text-slate-400 group-hover/map:text-white transition-colors" data-id="element-47" />
            </button>
          </div>
        </div>
        {/* KPI Metrics - Simplified without sparklines */}
        <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-48">
          {/* Active Jobs */}
          <div className="bg-slate-950/60 border border-slate-800/60 rounded-xl p-4 text-center" data-id="element-49">
            <div className="text-xs text-slate-500 mb-2 font-semibold uppercase tracking-wider" data-id="element-50">
              Active Jobs
            </div>
            <div className="text-3xl font-bold text-white" data-id="element-51">
              {account.kpis.active_jobs}
            </div>
          </div>
          {/* Open Quotes */}
          <div className="bg-slate-950/60 border border-slate-800/60 rounded-xl p-4 text-center" data-id="element-52">
            <div className="text-xs text-slate-500 mb-2 font-semibold uppercase tracking-wider" data-id="element-53">
              Open Quotes
            </div>
            <div className="text-3xl font-bold text-white" data-id="element-54">
              {account.kpis.open_quotes}
            </div>
          </div>
          {/* SLA Alerts */}
          <div className="bg-slate-950/60 border border-slate-800/60 rounded-xl p-4 text-center" data-id="element-55">
            <div className="text-xs text-slate-500 mb-2 font-semibold uppercase tracking-wider" data-id="element-56">
              SLA Alerts
            </div>
            <div className={`text-3xl font-bold ${account.kpis.sla_alerts > 0 ? 'text-red-400' : 'text-green-400'}`} data-id="element-57">
              {account.kpis.sla_alerts}
            </div>
          </div>
        </div>
        {/* Bottom Summary */}
        <div className="flex items-center gap-6 text-sm text-slate-500" data-id="element-58">
          <span data-id="element-59">{productCount} products</span>
          <span data-id="element-60">{teamCount} teams</span>
          <span data-id="element-61">{account.kpis.active_jobs} jobs</span>
        </div>
      </div>
    </button>;
}